import { Tooltip } from "@mantine/core";
import { NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { numericFormatter } from "react-number-format";
import { getDefaultNumericFormatterProps } from "../../../../../../../utils/getDefaultNumericFormatterProps";

/**
 * Custom node view for displaying a custom value within a formula template.
 * It shows the custom value's name (or actual value, if set) and focus highlighting.
 * For dynamic updating it listens to editor state changes.
 */
function CustomValueNodeView({
  node,
  editor,
  updateAttributes
}: ReactNodeViewProps<HTMLLabelElement>) {
  const id = node.attrs.id as string;

  const updateTriggered = useRef(false);
  const [nodeState, setNodeState] = useState({
    label: (node.attrs.label as string) || id,
    focussed: false
  });

  useEffect(() => {
    function handler() {
      if (updateTriggered.current) return;
      updateTriggered.current = true; // prevent infinite recursion due to `updateAttributes` below

      const newValue = editor.storage.customValueInTemplate.values[id];
      const newState = {
        label: newValue
          ? numericFormatter(newValue, getDefaultNumericFormatterProps())
          : id,
        focussed: editor.storage.customValueInTemplate.focussed[id] || false
      };
      setNodeState(newState);

      // store the updated label in the node as well, so that returns correctly in editor.getText()
      updateAttributes({ label: newState.label });

      updateTriggered.current = false;
    }

    // Since the ReactNodeViewRenderer is optimized for performance internally,
    // it doesn't re-render automatically on editor state changes.
    // Therefore we can't use the usual `useEditorState` hook here, but need to listen explicitly.
    editor.on("update", handler);
    return () => {
      editor.off("update", handler);
    };
  }, [editor, id, updateAttributes]);

  return (
    <NodeViewWrapper
      as="span"
      contentEditable={false}
      data-id={id}
      data-type="customValueInTemplate"
      data-value={nodeState.label}
    >
      <Tooltip disabled={id === nodeState.label} label={id}>
        <span
          className={classNames("expression-custom-value", {
            focussed: nodeState.focussed
          })}
        >
          {nodeState.label}
        </span>
      </Tooltip>
    </NodeViewWrapper>
  );
}

export { CustomValueNodeView };
